/**
 * Record Audio Element Editor
 * Configuration-only editor for the record-audio element.
 * Recording happens in the Player component (preview / student-facing).
 */

import * as React from 'react';
import {
    cn,
    Checkbox,
    Input,
    Label,
    RichTextEditor,
    createEmptyDocument,
    isTiptapDocument,
    createParagraphContent,
} from '@ai-content-hub/content-kit';
import type { ElementEditorProps, TiptapJSON } from '@ai-content-hub/content-kit';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';
import { recordAudioElementSchema, type RecordAudioElementData } from './schema';

export interface RecordAudioEditProps extends ElementEditorProps<RecordAudioElementData> {
    /** ELMS-2369: speaking always AI-grades, so its editor hides the legacy checkbox. Default true. */
    showAutoGrading?: boolean;
}

const RECORDING_TIME_MIN = 10;
const RECORDING_TIME_MAX = 300;

/**
 * EAA-1890: the panel no longer paints a surface, so the fields' `ring-1` is what
 * separates them from the page — keep the ring even though the fill now has nothing
 * to contrast against.
 */
const fieldInsetClass =
    'border-0 bg-background/10 text-foreground shadow-sm ring-1 ring-border/70 dark:bg-muted/40 dark:ring-border';

const checkboxInsetClass =
    'size-[18px] shrink-0 cursor-pointer bg-background/10 shadow-sm ring-2 ring-border/60 dark:bg-muted/40 dark:ring-border';

function clampRecordingSeconds(n: number): number {
    return Math.min(RECORDING_TIME_MAX, Math.max(RECORDING_TIME_MIN, n));
}

/** Convert a string | TiptapJSON title to a TiptapJSON value for the editor. */
function titleToTiptap(title: string | TiptapJSON | undefined): TiptapJSON {
    if (title && typeof title === 'object' && isTiptapDocument(title)) {
        return title;
    }
    if (typeof title === 'string' && title.trim()) {
        return createParagraphContent(title);
    }
    return createEmptyDocument();
}

export const RecordAudioEdit: React.FC<RecordAudioEditProps> = ({
    value,
    onChange,
    errors,
    disabled,
    className,
    config,
    schema: schemaProp,
    language,
    showAutoGrading = true,
}) => {
    const schema = schemaProp ?? recordAudioElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const [durationText, setDurationText] = React.useState(() => String(value.maxDuration ?? 60));

    React.useEffect(() => {
        setDurationText(String(value.maxDuration ?? 60));
    }, [value.maxDuration]);

    const handleFieldChange = React.useCallback(
        <K extends keyof RecordAudioElementData>(
            field: K,
            fieldValue: RecordAudioElementData[K]
        ) => {
            onChange({
                ...value,
                [field]: fieldValue,
            });
        },
        [value, onChange]
    );

    const autoGrading = value.autoGrading ?? false;

    return (
        <div className={cn('record-audio-edit', className)}>
            {/* EAA-1890: no `bg-card`/`shadow-sm`/`rounded-lg` — the recording section is
                the whole detail panel, so a tinted card inside a card just put a second
                surface behind every field. The host (SpeakingEdit) owns the surface. */}
            <div className="record-audio-edit-panel space-y-6 text-card-foreground">
                <div className="record-audio-edit-fields space-y-5">
                    <div className="record-audio-edit-question space-y-2">
                        <Label
                            htmlFor="record-audio-question"
                            className="text-sm font-medium text-card-foreground"
                        >
                            {t('question')}{' '}
                            <span className="text-destructive" aria-hidden>
                                *
                            </span>
                        </Label>
                        <RichTextEditor
                            id="record-audio-question"
                            value={titleToTiptap(value.title)}
                            onChange={(doc: TiptapJSON) => handleFieldChange('title', doc)}
                            placeholder={t('recordAudioQuestionPlaceholder')}
                            readOnly={disabled}
                            minHeight={100}
                            className={cn(
                                'record-audio-edit-question-input',
                                fieldInsetClass
                            )}
                            error={Boolean(errors?.title)}
                        />
                        {errors?.title && (
                            <p className="record-audio-edit-question-error text-sm text-destructive">
                                {errors.title}
                            </p>
                        )}
                    </div>

                    <div className="record-audio-edit-recording-time space-y-2">
                        <Label
                            htmlFor="record-audio-max-duration"
                            className="text-sm font-medium text-card-foreground"
                        >
                            {t('recordAudioRecordingTime')}
                        </Label>
                        <div className="record-audio-edit-recording-time-row flex flex-wrap items-center gap-3">
                            <Input
                                id="record-audio-max-duration"
                                type="number"
                                min={RECORDING_TIME_MIN}
                                max={RECORDING_TIME_MAX}
                                step={1}
                                value={durationText}
                                onChange={(e) => {
                                    const next = e.target.value;
                                    setDurationText(next);
                                    const n = parseInt(next, 10);
                                    if (!Number.isNaN(n)) {
                                        handleFieldChange('maxDuration', clampRecordingSeconds(n));
                                    }
                                }}
                                onBlur={() => {
                                    const n = parseInt(durationText, 10);
                                    const clamped = Number.isNaN(n)
                                        ? clampRecordingSeconds(value.maxDuration ?? 60)
                                        : clampRecordingSeconds(n);
                                    setDurationText(String(clamped));
                                    handleFieldChange('maxDuration', clamped);
                                }}
                                disabled={disabled}
                                className={cn(
                                    'record-audio-edit-duration-input h-10 w-28',
                                    fieldInsetClass
                                )}
                            />
                            <span className="record-audio-edit-recording-time-hint text-sm text-muted-foreground">
                                {t('recordAudioSecondsHint')}
                            </span>
                        </div>
                        {errors?.maxDuration && (
                            <p className="text-sm text-destructive">{errors.maxDuration}</p>
                        )}
                    </div>
                </div>

                {showAutoGrading && (
                <>
                {/* EAA-1890: the separator and the boxed, tinted wrapper around this
                    checkbox are gone. A rule plus a filled card read as a section of its
                    own, but it is one checkbox belonging to the fields above it — and the
                    grading-type field that follows is gated on it, so a box drawn round
                    only the checkbox implied the two were unrelated. */}
                <div className="record-audio-edit-auto-grade-wrap">
                    <div className="record-audio-edit-auto-grade flex items-center gap-3">
                        <Checkbox
                            id="record-audio-auto-grading"
                            checked={autoGrading}
                            onCheckedChange={(checked) =>
                                handleFieldChange('autoGrading', checked === true)
                            }
                            disabled={disabled}
                            className={cn(
                                'record-audio-edit-auto-grade-checkbox',
                                checkboxInsetClass
                            )}
                            aria-describedby="record-audio-auto-grading-label"
                        />
                        <Label
                            id="record-audio-auto-grading-label"
                            htmlFor="record-audio-auto-grading"
                            className="record-audio-edit-auto-grade-label cursor-pointer text-sm font-medium leading-snug text-card-foreground"
                        >
                            {t('recordAudioAutoGrading')}
                        </Label>
                    </div>
                </div>
                {errors?.autoGrading && (
                    <p className="text-sm text-destructive">{errors.autoGrading}</p>
                )}
                </>
                )}
            </div>
        </div>
    );
};

export default RecordAudioEdit;
